import {
  Component,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { FormGroup, ControlContainer, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HardwareVariant } from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { DealerClaimType } from '@core/enums/dealer-claim-type';
import { MobilePhoneFormControls } from '../mobilephone-details.component';
import { IconNames } from '@shared/enums/icon-names';
import { MobilephoneSearchHelperService } from '@app/sales/modules/mobilephone/services/mobilephone-search-helper.service';
import { convertStorageCapacityToGB } from '@app/sales/modules/mobilephone/mobilephone-utils';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { select } from '@ngxs/store';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { MatIcon } from '@angular/material/icon';
import { AllowSettingsDirective } from '@app/shared/directives/allow-settings.directive';
import { IceRadioButtonComponent } from '@app/shared/components/atoms/ice-radio-button/ice-radio-button.component';

type VariantStatus = {
  value: string;
  disabled: boolean;
  disabledText?: string;
};

@Component({
  selector: 'app-mobilephone-variant-options',
  templateUrl: './mobilephone-variant-options.component.html',
  styleUrls: ['./mobilephone-variant-options.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [IceRadioButtonComponent, FormsModule, ReactiveFormsModule, AllowSettingsDirective, MatIcon, SpinnerComponent],
})
export class MobilephoneVariantOptionsComponent implements OnChanges, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private formGroupDirective = inject(FormGroupDirective);

  MobilePhoneFormControls = MobilePhoneFormControls;
  IconNames = IconNames;

  isRetail = select(DealerStateSelectors.hasPhysicalSigningClaim);

  @Input() mobilePhoneGroupVariants: HardwareVariant[];
  @Input() selectedVariant: HardwareVariant;
  @Output() variantChanged = new EventEmitter();

  form: FormGroup;
  currentSelected: HardwareVariant;
  subscriptions$: Subscription[] = [];
  DealerClaimType = DealerClaimType;
  colorOptions: VariantStatus[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes.selectedVariant?.currentValue || {}).length > 0) {
      const offering = changes.selectedVariant.currentValue as HardwareVariant;
      this.currentSelected = this.mobilePhoneGroupVariants.find(
        x => x.color === offering.color && x.memorySize === offering.memorySize
      );
      const isAvailableForOrder =
        this.isRetail() || this.currentSelected?.quantityInStock > 0 || this.currentSelected?.backorderInfo?.allow;
      this.form = this.formGroupDirective.form;
      if (!isAvailableForOrder) {
        const availableOffering = this.mobilePhoneGroupVariants.find(
          x => (x.quantityInStock > 0 || x.backorderInfo?.allow) && x.memorySize === offering.memorySize
        );
        this.form.get(MobilePhoneFormControls.Color).setValue(availableOffering?.color);
        this.form.get(MobilePhoneFormControls.Memory).setValue(availableOffering?.memorySize);
      } else {
        this.form.get(MobilePhoneFormControls.Color).setValue(this.currentSelected?.color);
        this.form.get(MobilePhoneFormControls.Memory).setValue(this.currentSelected?.memorySize);
      }
      this.subscriptions$.push(
        this.form.controls.color.valueChanges.subscribe(value => {
          if (value !== this.currentSelected?.color) {
            const newOffering =
              this.mobilePhoneGroupVariants.find(
                x => x.color === value && x.memorySize == this.currentSelected.memorySize
              ) ?? this.mobilePhoneGroupVariants.find(x => x.color === value);
            this.variantChanged.emit(newOffering);
            this.changeDetectorRef.detectChanges();
          }
        })
      );
      this.subscriptions$.push(
        this.form.controls.memory.valueChanges.subscribe(value => {
          if (value !== this.currentSelected?.memorySize) {
            const newOffering =
              this.mobilePhoneGroupVariants.find(x => x.memorySize === value && x.color == this.currentSelected.color) ??
              this.mobilePhoneGroupVariants.find(x => x.memorySize === value);
            this.variantChanged.emit(newOffering);
            this.changeDetectorRef.detectChanges();
          }
        })
      );
    }
  }

  get colors(): VariantStatus[] {
    const selectedMemorySize = this.form.get(MobilePhoneFormControls.Memory).value;
    const uniqueColors = new Set(this.mobilePhoneGroupVariants.map(item => item.color).sort());
    return Array.from(uniqueColors).map(color => {
      const variants = this.mobilePhoneGroupVariants.filter(item => item.color === color);
      const memorySizesAvailableForColor = variants
        .filter(item => item.quantityInStock > 0 || item.backorderInfo?.allow)
        .map(item => item.memorySize)
        .sort((a, b) => convertStorageCapacityToGB(a) - convertStorageCapacityToGB(b));

      const isColorAvailableForSelectedMemorySize = variants.some(
        item => item.memorySize === selectedMemorySize && (item.quantityInStock > 0 || item.backorderInfo?.allow)
      );
      const isDisabled = !this.isRetail() && !isColorAvailableForSelectedMemorySize;

      const memorySizesString =
        memorySizesAvailableForColor.length == 0
          ? 'Ikke tilgjengelig'
          : memorySizesAvailableForColor.length > 1
            ? `Kun tilgjengelig for ${memorySizesAvailableForColor.slice(0, -1).join(', ')} og ${memorySizesAvailableForColor.slice(-1)}`
            : `Kun tilgjengelig for ${memorySizesAvailableForColor.join(', ')}`;

      return {
        disabled: isDisabled,
        value: color,
        disabledText: isDisabled ? memorySizesString : null,
      };
    });
  }

  get memorySizes(): VariantStatus[] {
    const uniqueMemorySizes = new Set(this.mobilePhoneGroupVariants.map(item => item.memorySize).sort());
    const memorySizes = Array.from(uniqueMemorySizes).map(memorySize => {
      const variants = this.mobilePhoneGroupVariants.filter(item => item.memorySize === memorySize);
      const hasAnyVariantsInStock = variants.some(item => item.quantityInStock > 0 || item.backorderInfo?.allow);
      const isDisabled = !this.isRetail() && !hasAnyVariantsInStock;

      return {
        disabled: isDisabled,
        value: memorySize,
        disabledText: isDisabled ? 'Ikke tilgjengelig' : null,
      };
    });

    return memorySizes.sort((a, b) => convertStorageCapacityToGB(a.value) - convertStorageCapacityToGB(b.value));
  }

  get variantStockInfo(): string {
    const quantityInStock = this.selectedVariant?.quantityInStock ?? 0;
    if (quantityInStock <= 0) {
      return null;
    }
    if (quantityInStock >= 1 && quantityInStock < 5) {
      return 'Færre enn 5 på lager';
    }

    if (quantityInStock >= 5 && quantityInStock <= 20) {
      return `${quantityInStock} på lager`;
    }

    return '20+ på lager';
  }

  get canBeBackOrdered(): boolean {
    return this.selectedVariant?.backorderInfo?.allow;
  }

  get showVariantOptions(): boolean {
    return MobilephoneSearchHelperService.hasVariantOptions(this.selectedVariant);
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
